// @ts-nocheck
import {
  useEffect,
  useRef,
  useState,
  Ref,
  useMemo,
  useCallback,
  Fragment,
} from "react";
import { useNavigationStore } from "src/store/navigationStore";

import { Canvas, useFrame, RootState } from "@react-three/fiber";
import { Splat, useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { Group } from "three";

import { GLB_AVATAR } from "src/const/navigationConst";

import { useModelViewerStore } from "@/store/modelViewerStore";

import {
  createRadialScriptFast,
  SplatLodComponent,
  SplatSogComponent,
} from "@/components/playcanvas/playcanvasComponents";

import {
  roundToDecimals,
  vec3Distance,
  hexToRgbArray,
  getYAngleDegree,
  angleDifferenceABtoAC,
  wrapAngle,
  calculateTargetPosition,
  normalizeRotation,
} from "src/utils/game";
import * as pc from "playcanvas";
import { OrbitControls } from "@playcanvas/react/scripts";
import {
  useTexture,
  useMaterial,
  useEnvAtlas,
  useApp,
  useSplat,
  useModel,
  useAppEvent,
} from "@playcanvas/react/hooks";
import { Application, Entity, Gltf } from "@playcanvas/react";
import {
  Render,
  Camera,
  Light,
  GSplat,
  Script,
  Environment,
} from "@playcanvas/react/components";
import { CameraControls } from "playcanvas/scripts/esm/camera-controls.mjs";

import WatermarkScreen from "src/components/watermark";
import { useLodSplat } from "src/utils/useLodSplat";
import LottieLoading from "src/components/lottie/lottie-loading";

import { GsplatRevealRadial } from "src/extra_js/reveal-radial.mjs";

import { getBaseUrl } from "src/utils/map-util";

export default function Reconstruction() {
  const interruptAutoPlay = useModelViewerStore(
    (state) => state.interruptAutoPlay,
  );
  const setProgress = useModelViewerStore((state) => state.setProgress);

  const setCameraPosition = useModelViewerStore(
    (state) => state.setCameraPosition,
  );
  const setCameraLookingPosition = useModelViewerStore(
    (state) => state.setCameraLookingPosition,
  );
  const setCameraAngle = useModelViewerStore((state) => state.setCameraAngle);

  const setIsModelLoaded = useModelViewerStore(
    (state) => state.setIsModelLoaded,
  );
  const setErrorMessage = useModelViewerStore((state) => state.setErrorMessage);

  // const moveSpeedRef = useRef<number>(2);
  const moveSpeedRef = useRef<number>(0.5);
  const rotateSpeedRef = useRef<number>(20);

  const defaultPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
  const defaultTargetRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, -1));

  const targetAltitudeRef = useRef<number>(0);

  const latestCurrentLevelNameRef = useRef<string>("");

  const mainObjectEntityRef = useRef();
  const cameraScriptRef = useRef();
  const hiddenNearRef = useRef<pc.Entity>(null);
  const hiddenFarRef = useRef<pc.Entity>(null);
  const firstPersonCameraEntityRef = useRef();
  const thirdPersonCameraEntityRef = useRef();
  const latestCameraModeRef = useRef<number>(0);

  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const eulerRef = useRef(new pc.Vec3(0, 0, 0));

  const isMovingRef = useRef<boolean>(false);
  const isRotatingRef = useRef<boolean>(false);

  const latestPositionJoystickData = useRef<number[]>([0, 0]);
  const latestRotationJoystickData = useRef<number[]>([0, 0]);
  const latestRotationScreenData = useRef<number[]>([0, 0]);

  const latestResetPositionRotationEvent = useRef<number>(0);
  const latestResetOnlyRotationEvent = useRef<number>(0);

  const latestVenueModelConfigRef = useRef<VenueModelConfig | null>(null);

  const isInstantUpdate = useRef<boolean>(false);

  const latestModelJsonData = useRef<any>(null);
  const latestCustomPathJsonData = useRef<any>(null);

  const reconstructionStepData = {
    folderPath: "reconstruction_step/xiqu/models/xiqu_p",
    steps: 20,
    scale: 100,
  };

  const pathPosition = useRef<number[][]>([
    [
      -1750 / reconstructionStepData.scale,
      200 / reconstructionStepData.scale,
      -1450 / reconstructionStepData.scale,
    ],
    [
      -1750 / reconstructionStepData.scale,
      200 / reconstructionStepData.scale,
      -1450 / reconstructionStepData.scale,
    ],
    [
      -1500 / reconstructionStepData.scale,
      200 / reconstructionStepData.scale,
      -1400 / reconstructionStepData.scale,
    ],
  ]);

  const pathRotation = useRef<number[][]>([
    [-30, -45, 0],
    [-26, -100, 0],
    [-10, -160, 0],
  ]);

  const latestProgressRef = useRef(0);

  const isDisplayAvatarRef = useRef<boolean>(false);

  const AUTOPLAY_SPEED = 4;
  const autoPlaySpeedRef = useRef<number>(AUTOPLAY_SPEED);
  const isAutoPlayingRef = useRef<boolean>(false);
  const autoPlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const gsplatAllRef = useRef({});

  const splatFormat = useRef("");

  function stopAutoPlay() {
    isAutoPlayingRef.current = false;
    if (autoPlayTimeoutRef.current) {
      clearTimeout(autoPlayTimeoutRef.current);
      autoPlayTimeoutRef.current = null;
    }
  }
  function resumeAutoPlay(milliseconds: number) {
    if (milliseconds > 0) {
      stopAutoPlay();
    }
    autoPlayTimeoutRef.current = setTimeout(() => {
      autoPlayTimeoutRef.current = null;
      isAutoPlayingRef.current = true;
    }, milliseconds);
  }

  function interpolateVector3Array(points, percentage) {
    if (percentage <= 0) return points[0];
    if (percentage >= 1) return points[points.length - 1];

    const segments = points.length - 1;
    const scaledPosition = percentage * segments;
    const segmentIndex = Math.floor(scaledPosition);
    const segmentFraction = scaledPosition - segmentIndex;

    const start = points[segmentIndex];
    const end = points[segmentIndex + 1];

    return [
      start[0] + (end[0] - start[0]) * segmentFraction,
      start[1] + (end[1] - start[1]) * segmentFraction,
      start[2] + (end[2] - start[2]) * segmentFraction,
    ];
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() === "p") {
        console.log(cameraScriptRef.current.entity.position);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  function useQueryParams() {
    return useMemo(() => new URLSearchParams(window.location.search), []);
  }

  const DataController = () => {
    const resetPositionRotationEvent = useModelViewerStore(
      (state) => state.resetPositionRotationEvent,
    );
    useEffect(() => {
      latestResetPositionRotationEvent.current = resetPositionRotationEvent;
    }, [resetPositionRotationEvent]);

    const resetOnlyRotationEvent = useModelViewerStore(
      (state) => state.resetOnlyRotationEvent,
    );
    useEffect(() => {
      latestResetOnlyRotationEvent.current = resetOnlyRotationEvent;
    }, [resetOnlyRotationEvent]);

    const positionJoystickData = useModelViewerStore(
      (state) => state.positionJoystickData,
    );
    const rotationJoystickData = useModelViewerStore(
      (state) => state.rotationJoystickData,
    );

    useEffect(() => {
      latestPositionJoystickData.current = [
        positionJoystickData[0],
        positionJoystickData[1],
      ];
    }, [positionJoystickData]);

    useEffect(() => {
      latestRotationJoystickData.current = [
        rotationJoystickData[0],
        rotationJoystickData[1],
      ];
    }, [rotationJoystickData]);

    const progress = useModelViewerStore((state) => state.progress);

    useEffect(() => {
      latestProgressRef.current = progress;
      const newPosition = interpolateVector3Array(
        pathPosition.current,
        progress,
      );
      const newRotation = interpolateVector3Array(
        pathRotation.current,
        progress,
      );

      if (mainObjectEntityRef.current) {
        mainObjectEntityRef.current.setPosition(
          newPosition[0],
          newPosition[1],
          newPosition[2],
        );
        mainObjectEntityRef.current.setLocalEulerAngles(
          newRotation[0],
          newRotation[1],
          newRotation[2],
        );
        isMovingRef.current = true;
        isRotatingRef.current = true;
      }

      eulerRef.current.x = newRotation[0];
      eulerRef.current.y = newRotation[1];
      eulerRef.current.z = newRotation[2];
    }, [progress]);

    const interruptAutoPlayEvent = useModelViewerStore(
      (state) => state.interruptAutoPlayEvent,
    );
    useEffect(() => {
      if (interruptAutoPlayEvent != -1) {
        //resumeAutoPlay(2000);
      }
    }, [interruptAutoPlayEvent]);

    const cameraMode = useModelViewerStore((state) => state.cameraMode);
    useEffect(() => {
      latestCameraModeRef.current = cameraMode;
    }, [cameraMode]);

    return <></>;
  };

  const MainCamera = () => {
    const app = useApp();
    const cameraRef = useRef();
    let _localProgress;
    let _localCameraMode;
    let isCameraModeUpdated;
    let nearestFixedCameraPosition;

    let finalCameraPosition;
    let finalCameraLookingPosition;

    app.on("update", function (dt) {
      if (cameraScriptRef.current == null) {
        return;
      }

      isCameraModeUpdated = _localCameraMode != latestCameraModeRef.current;
      _localCameraMode = latestCameraModeRef.current;

      if (
        isInstantUpdate.current ||
        isCameraModeUpdated ||
        isMovingRef.current ||
        isRotatingRef.current
      ) {
        const newPosition = firstPersonCameraEntityRef.current.getPosition();
        cameraScriptRef.current.reset(
          mainObjectEntityRef.current.getPosition(),
          newPosition,
        );
        isMovingRef.current = false;
        isRotatingRef.current = false;
        isInstantUpdate.current = false;
      }
    });

    return (
      <Entity>
        <Camera fov={60} />
        <Script
          ref={cameraScriptRef}
          script={CameraControls}
          enableFly={false}
        />
        <Entity ref={hiddenNearRef} position={[0, 0, 0]}></Entity>
        <Entity ref={hiddenFarRef} position={[0, 0, -0.5]}></Entity>
      </Entity>
    );
  };

  const MainObject = () => {
    const queryParams = useQueryParams();
    const modelLink = queryParams.get("model");
    const app = useApp();

    const defaultPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const defaultTargetRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, -1));

    const setInitPositionTarget = (
      positionX: number,
      positionY: number,
      positionZ: number,
      targetX: number,
      targetY: number,
      targetZ: number,
    ) => {
      defaultPositionRef.current.set(-positionX, positionY, -positionZ);
      defaultTargetRef.current.set(-targetX, targetY, -targetZ);
      mainObjectEntityRef.current.setPosition(defaultPositionRef.current);
      mainObjectEntityRef.current.lookAt(defaultTargetRef.current);
      const initAngles = mainObjectEntityRef.current.getEulerAngles();
      if (initAngles.z < 90 && initAngles.z > -90) {
        console.log("type A");
        eulerRef.current.set(initAngles.x, initAngles.y, 0);
      } else if (initAngles.z > 90 || initAngles.z < -90) {
        console.log("type B");
        eulerRef.current.set(initAngles.x - 180, 180 - initAngles.y, 0);
      } else {
        console.log("type C");
        eulerRef.current.set(0, 0, 0);
      }
      isInstantUpdate.current = true;
    };

    const handleUpdate = useCallback((dt: number) => {
      if (mainObjectEntityRef.current == null) {
        return;
      }
      if (
        latestRotationScreenData.current[0] != 0 ||
        latestRotationScreenData.current[1] != 0
      ) {
        isRotatingRef.current = true;
        eulerRef.current.y +=
          -latestRotationScreenData.current[0] * rotateSpeedRef.current * dt; // Yaw
        eulerRef.current.x +=
          -latestRotationScreenData.current[1] * rotateSpeedRef.current * dt; // Pitch

        eulerRef.current.x = pc.math.clamp(eulerRef.current.x, -45, 45);
        eulerRef.current.y = wrapAngle(eulerRef.current.y);

        mainObjectEntityRef.current.setLocalEulerAngles(eulerRef.current);
      }
    }, []);
    useAppEvent("update", handleUpdate);

    useEffect(() => {
      mainObjectEntityRef.current.setPosition(
        pathPosition.current[0][0],
        pathPosition.current[0][1],
        pathPosition.current[0][2],
      );
      mainObjectEntityRef.current.setLocalEulerAngles(
        pathRotation.current[0][0],
        pathRotation.current[0][1],
        pathRotation.current[0][2],
      );
    }, []);

    return (
      <Entity ref={mainObjectEntityRef}>
        <Entity
          ref={firstPersonCameraEntityRef}
          position={[0, 0, 0.001]}
        ></Entity>
        <Entity
          ref={thirdPersonCameraEntityRef}
          position={[0, 0.5, 4]}
        ></Entity>
      </Entity>
    );
  };

  const ReconstructionSteps = () => {
    const app = useApp();
    const baseUrl = getBaseUrl();
    const baseModelUrl = baseUrl + reconstructionStepData.folderPath;
    const stepCount = reconstructionStepData.steps;
    return (
      <>
        {Array.from({ length: stepCount }, (_, index) => (
          <Fragment key={index}>
            <EachReconstructionStep
              app={app}
              index={index}
              baseModelUrl={baseModelUrl}
              displayProgress={index / stepCount}
            />
          </Fragment>
        ))}
      </>
    );
  };

  const EachReconstructionStep = ({
    app,
    index,
    baseModelUrl,
    displayProgress,
  }) => {
    const splatRef = useRef(null);
    const isShowReveal = useRef(false);
    const targetScale = useRef(1);
    const entityRef = useRef(null);
    const opacityRef = useRef(0);
    const callbackReadyFunction = (splat) => {
      splatRef.current = splat;
      console.log("steps sog ready");
      setTimeout(() => {
        // splat.gsplat.enabled = false;
        splat.gsplat.enabled = true;
        targetScale.current = splat.getLocalScale().x;

        // const opacity = 0.05;
        // if (
        //   splat.gsplat == null ||
        //   splat.gsplat.setWorkBufferModifier == null
        // ) {
        //   return;
        // }
        // splat.gsplat.setWorkBufferModifier({
        //   glsl: `
        //     void modifySplatCenter(inout vec3 center) {}
        //     void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {}
        //     void modifySplatColor(vec3 center, inout vec4 color) { color.a *= ${opacity.toFixed(1)}; }
        //   `,
        // });
        // console.log(splat);

        //splat.gsplat.enabled = true;
        //createRadialScriptFast(mainObjectEntityRef.current , splat);
      }, 200);
    };
    const handleUpdate = useCallback((dt: number) => {
      if (splatRef.current && latestProgressRef.current != null) {
        let isShow = false;
        if (index == 0) {
          isShow = latestProgressRef.current > 0.02;
        } else {
          isShow = latestProgressRef.current > displayProgress;
        }
        
        //splatRef.current.gsplat.enabled = isShow;

        if (
          splatRef.current.gsplat == null ||
          splatRef.current.gsplat.setWorkBufferModifier == null
        ) {
          return;
        }
        if (!isShow) {
          opacityRef.current -= dt * 2;
          opacityRef.current = opacityRef.current < 0 ? 0 : opacityRef.current;

        } else {
          opacityRef.current += dt * 2;
          opacityRef.current = opacityRef.current > 1 ? 1 : opacityRef.current;
        }

        splatRef.current.gsplat.setWorkBufferModifier({
          glsl: `
            void modifySplatCenter(inout vec3 center) {}
            void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {}
            void modifySplatColor(vec3 center, inout vec4 color) { color.a *= ${opacityRef.current.toFixed(1)}; }
          `,
        });

        // if (!isShow) {
        //   let currentPosition = entityRef.current.getLocalPosition().y;
        //   console.log(currentPosition);
        //   if (currentPosition < 3) {
        //     const newPosition = currentPosition + dt * 10;
        //     entityRef.current.setLocalPosition(0, newPosition, 0);
        //   } else {
        //     entityRef.current.setLocalPosition(0, 3, 0);
        //     splatRef.current.gsplat.enabled = false;
        //   }
        // } else {
        //   splatRef.current.gsplat.enabled = true;
        //   let currentPosition = entityRef.current.getLocalPosition().y;
        //   if (currentPosition > 0) {
        //     const newPosition = currentPosition - dt * 10;
        //     entityRef.current.setLocalPosition(0, newPosition, 0);
        //   } else {
        //     entityRef.current.setLocalPosition(0, 0, 0);
        //   }
        // }

        // if (!isShow) {
        //   let currentScale = entityRef.current.getLocalScale().y;
        //   if(currentScale > 0){
        //     const newScale = currentScale - dt * 3;
        //     entityRef.current.setLocalScale(1,newScale,1);
        //   }
        //   else{
        //     entityRef.current.setLocalScale(1,0,1);
        //     splatRef.current.gsplat.enabled = false;
        //   }
        // } else {
        //   let currentScale = entityRef.current.getLocalScale().y;
        //   if(currentScale < 1){
        //     const newScale = currentScale + dt * 3;
        //     entityRef.current.setLocalScale(1,newScale,1);
        //   }
        //   else{
        //     entityRef.current.setLocalScale(1,1,1);
        //   }
        // }

        //splatRef.current.setLocalScale(0.01,0.005,0.01);
      }
    }, []);
    useAppEvent("update", handleUpdate);
    return (
      <Entity ref={entityRef}>
        <SplatSogComponent
          app={app}
          url={baseModelUrl + (index + 1) + ".sog"}
          rotation={[180, 0, 0]}
          scale={[0.01, 0.01, 0.01]}
          callbackReady={callbackReadyFunction}
        />
      </Entity>
    );
  };

  const PlaycanvasScene = () => {
    const app = useApp();
    const newProgress = useRef(0);
    const handleUpdate = useCallback((dt: number) => {
      if (isAutoPlayingRef.current) {
        newProgress.current = latestProgressRef.current + 0.0005;
        newProgress.current = newProgress.current > 1 ? 1 : newProgress.current;
        setProgress(newProgress.current);
        if (newProgress.current == 1) {
          stopAutoPlay();
        }
      }
    }, []);
    useAppEvent("update", handleUpdate);

    return (
      <>
        {/*{isSog ? <ModelSog /> : <></>}*/}
        {/*        {isSog ? <SingleSog /> : <></>}
        {isLod ? <SingleLod /> : <></>}*/}
        <ReconstructionSteps />
        <MainObject />
        <MainCamera />
      </>
    );
  };

  const Scene = () => {
    return (
      <Application>
        <PlaycanvasScene />
      </Application>
    );
  };

  const Loading = () => {
    const isModelLoaded = useModelViewerStore((state) => state.isModelLoaded);
    return !isModelLoaded ? (
      <div className="frosted-fullscreen flex flex-col items-center justify-center">
        <LottieLoading />
      </div>
    ) : (
      <></>
    );
  };

  const ErrorMessage = () => {
    const errorMessage = useModelViewerStore((state) => state.errorMessage);
    return errorMessage != "" ? (
      <div className="frosted-fullscreen flex flex-col items-center justify-center">
        {errorMessage}
      </div>
    ) : (
      <></>
    );
  };

  const setPositionEulerAngles = (
    positionX: number,
    positionY: number,
    positionZ: number,
    eulerAngleX: number,
    eulerAngleY: number,
    eulerAngleZ: number,
  ) => {
    if (mainObjectEntityRef.current) {
      mainObjectEntityRef.current.setPosition(positionX, positionY, positionZ);
      mainObjectEntityRef.current.setLocalEulerAngles(
        eulerAngleX,
        eulerAngleY,
        eulerAngleZ,
      );
      isMovingRef.current = true;
      isRotatingRef.current = true;
    }
  };
  console.log(
    "Model Viewer Page component rendered , if this keep showing, something wrong",
  );

  const DragController = () => {
    const isDraggingRef = useRef(false);
    const startPosRef = useRef({ x: 0, y: 0 });
    const previousPositionRef = useRef({ x: 0, y: 0 });
    const getClientPos = (e) => {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    };
    const handleStart = useCallback((e) => {
      const pos = getClientPos(e);
      isDraggingRef.current = true;
      previousPositionRef.current = { x: pos.x, y: pos.y };
    }, []);
    const handleMove = useCallback((e) => {
      if (!isDraggingRef.current) {
        return;
      }
      const pos = getClientPos(e);
      const xChange = pos.x - previousPositionRef.current.x;
      const yChange = pos.y - previousPositionRef.current.y;
      previousPositionRef.current = { x: pos.x, y: pos.y };
      latestRotationScreenData.current[0] = xChange * 0.5;
      latestRotationScreenData.current[1] = yChange * 0.5;
      interruptAutoPlay();
    }, []);
    const handleEnd = useCallback(() => {
      isDraggingRef.current = false;
      latestRotationScreenData.current[0] = 0;
      latestRotationScreenData.current[1] = 0;
    }, []);
    useEffect(() => {
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchend", handleEnd);
      return () => {
        window.removeEventListener("mouseup", handleEnd);
        window.removeEventListener("touchend", handleEnd);
      };
    }, []);

    return (
      <div
        className="fullscreen_div"
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onMouseUp={handleEnd}
        onTouchEnd={handleEnd}
      ></div>
    );
  };

  return (
    <div
      className="absolute top-0 left-0 w-full h-full"
      style={{ backgroundColor: "black" }}
    >
      {/*<Loading />*/}
      <ErrorMessage />
      <DataController />
      <Scene />
      <DragController />
      <WatermarkScreen />
    </div>
  );
}
